import { normalizeVariableName } from './names';
import {
  ARRAY_INDEX_ALL,
  isArrayAllIndex,
  isArrayVariable,
  joinArrayValuesForPrint,
} from './types';

/**
 * Parse a variable reference: `name`, `name[1]` (1-based), or `name[*]`
 * (all values). Whitespace around the name / brackets is tolerated; spaces
 * in the name become `_` (same as typed names elsewhere).
 */
export const parseVariableRef = (raw) => {
  const trimmed = String(raw ?? '')
    .trim()
    .replace(/[{}]/g, '');
  const all = trimmed.match(/^(.+?)\s*\[\s*(?:\*|printall)\s*\]\s*$/i);
  if (all) {
    const name = normalizeVariableName(all[1]).replace(/\s+/g, '_');
    if (name) {
      return { name, index: ARRAY_INDEX_ALL, ref: `${name}[*]`, all: true };
    }
  }
  const indexed = trimmed.match(/^(.+?)\s*\[\s*(\d+)\s*\]\s*$/);
  if (indexed) {
    const name = normalizeVariableName(indexed[1]).replace(/\s+/g, '_');
    const index = parseInt(indexed[2], 10);
    if (name && Number.isFinite(index) && index >= 1) {
      return { name, index, ref: `${name}[${index}]`, all: false };
    }
  }
  const name = normalizeVariableName(trimmed).replace(/\s+/g, '_');
  return { name, index: null, ref: name, all: false };
};

/** Canonical brace/pill ref: `name`, `name[1]`, or `name[*]`. */
export const formatVariableRef = (name, index = null) => {
  const n = normalizeVariableName(name).replace(/\s+/g, '_');
  if (!n) return '';
  if (isArrayAllIndex(index)) return `${n}[*]`;
  if (index != null && index >= 1) return `${n}[${index}]`;
  return n;
};

/** Raw (unexpanded) slot value for a row + optional 1-based / `*` index. */
export const getVariableSlotValue = (row, index = null) => {
  if (!row) return '';
  if (isArrayVariable(row)) {
    if (isArrayAllIndex(index)) return joinArrayValuesForPrint(row.values);
    if (index == null || index < 1) return '';
    const values = Array.isArray(row.values) ? row.values : [];
    if (index > values.length) return '';
    return String(values[index - 1] ?? '');
  }
  return String(row.value ?? '');
};
